import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
    LayoutDashboard,
    FolderOpen,
    Briefcase,
    Users,
    MessageSquare,
    FileText,
    BarChart,
    Mail,
    Settings,
    LogOut,
    ChevronsUpDown,
    User,
    Menu
} from 'lucide-react';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    SidebarTrigger,
    useSidebar,
} from "@/components/ui/sidebar"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/use-auth';

export function GlassmorphismSidebar() {
    const { user, signOut } = useAuth();
    const location = useLocation();
    const { state } = useSidebar();
    const isCollapsed = state === "collapsed";

    const navigation = [
        { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
        { name: 'Projetos', href: '/admin/projetos', icon: FolderOpen },
        { name: 'Serviços', href: '/admin/servicos', icon: Briefcase },
        { name: 'Clientes', href: '/admin/clientes', icon: Users },
        { name: 'Depoimentos', href: '/admin/depoimentos', icon: MessageSquare },
        { name: 'Conteúdo', href: '/admin/conteudo', icon: FileText },
        { name: 'Analytics', href: '/admin/analytics', icon: BarChart },
        { name: 'Contato', href: '/admin/contato', icon: Mail },
        { name: 'Configurações', href: '/admin/configuracoes', icon: Settings },
    ];

    const handleLogout = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Erro ao fazer logout:', error);
        }
    };

    return (
        <Sidebar collapsible="icon" className="border-r-0 bg-transparent">
            <div className="absolute inset-0 bg-background/60 backdrop-blur-xl border-r border-white/10" />

            {/* Header */}
            <SidebarHeader className="relative z-10 h-20 flex items-center justify-center border-b border-white/5 px-4 transition-all will-change-transform">
                <div className={cn(
                    "flex items-center gap-2 font-bold text-xl text-primary overflow-hidden transition-all duration-300",
                    isCollapsed ? "scale-0 w-0 opacity-0" : "scale-100 w-auto opacity-100"
                )}>
                    <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                        Cássio Lemos
                    </span>
                </div>
                {isCollapsed && (
                    <div className="absolute inset-0 flex items-center justify-center font-bold text-xl bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent transform transition-all duration-300">
                        CL
                    </div>
                )}
            </SidebarHeader>

            {/* Content */}
            <SidebarContent className="relative z-10 px-2 py-4 gap-2 scrollbar-none">
                <SidebarMenu>
                    {navigation.map((item) => {
                        const isActive = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));

                        return (
                            <SidebarMenuItem key={item.name}>
                                <SidebarMenuButton
                                    asChild
                                    isActive={isActive}
                                    tooltip={item.name}
                                    className={cn(
                                        "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 outline-none hover:bg-white/5",
                                        isActive
                                            ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white shadow-[0_0_20px_-5px_rgba(59,130,246,0.3)] border border-white/5"
                                            : "text-muted-foreground hover:text-white"
                                    )}
                                >
                                    <Link to={item.href}>
                                        <item.icon className={cn(
                                            "h-5 w-5 transition-transform duration-200 group-hover:scale-110",
                                            isActive ? "text-blue-400" : "text-muted-foreground group-hover:text-blue-300"
                                        )} />
                                        <span className={cn(
                                            "transition-all duration-300",
                                            isCollapsed ? "opacity-0 w-0" : "opacity-100"
                                        )}>
                                            {item.name}
                                        </span>
                                        {isActive && !isCollapsed && (
                                            <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                                        )}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        );
                    })}
                </SidebarMenu>
            </SidebarContent>

            {/* Footer */}
            <SidebarFooter className="relative z-10 p-2 border-t border-white/5 bg-black/10">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <SidebarMenuButton
                                    size="lg"
                                    className="group data-[state=open]:bg-white/5 hover:bg-white/5 rounded-xl transition-all"
                                >
                                    <div className="relative">
                                        <Avatar className="h-9 w-9 rounded-lg ring-2 ring-white/10 transition-all group-hover:ring-blue-500/50">
                                            <AvatarImage src="" alt={user?.email || "Avatar"} />
                                            <AvatarFallback className="rounded-lg bg-gradient-to-br from-blue-600 to-purple-700 text-white font-bold">
                                                {(user?.email?.charAt(0) || "U").toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-background rounded-full" />
                                    </div>

                                    <div className="grid flex-1 text-left text-sm leading-tight ml-1">
                                        <span className="truncate font-semibold text-white group-hover:text-blue-200 transition-colors">
                                            Admin
                                        </span>
                                        <span className="truncate text-xs text-muted-foreground group-hover:text-blue-300/70 transition-colors">
                                            {user?.email}
                                        </span>
                                    </div>
                                    <ChevronsUpDown className="ml-auto size-4 text-muted-foreground group-hover:text-white" />
                                </SidebarMenuButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                className="w-[--sidebar-width] min-w-56 rounded-xl border-white/10 bg-black/80 backdrop-blur-2xl shadow-2xl p-2"
                                side="bottom"
                                align="end"
                                sideOffset={4}
                            >
                                <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                                    Minha Conta
                                </div>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuGroup>
                                    <DropdownMenuItem className="focus:bg-white/10 focus:text-white cursor-pointer rounded-lg my-1">
                                        <User className="mr-2 h-4 w-4" />
                                        Perfil
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="focus:bg-white/10 focus:text-white cursor-pointer rounded-lg my-1">
                                        <Settings className="mr-2 h-4 w-4" />
                                        Configurações
                                    </DropdownMenuItem>
                                </DropdownMenuGroup>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem
                                    onClick={handleLogout}
                                    className="text-red-400 focus:text-red-300 focus:bg-red-500/10 cursor-pointer rounded-lg my-1"
                                >
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Sair da Conta
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}
